import { memo, useMemo } from "react";
import { Link } from "react-router-dom";
import useCurrentUser from "../../hooks/useCurrentUser";
import { sortBy } from "lodash-es";
import clsx from "clsx";
import BankCard from "../BankCard/BankCard";
import { isEven, rgbaObjectToString } from "../../helpers/utils";
import { motion } from "framer-motion";
import useAccountLocalStorage from "../../hooks/useAccountLocalStorage";
import { MdAdd } from "react-icons/md";
import { ICON_SIZES } from "src/helpers/constants";
import { useTranslation } from "react-i18next";

interface IAccountsListVertical {
  linkTo: string;
  animate?: boolean;
  showDisabled?: boolean;
  updateLocalStorage?: boolean;
}

const AccountsListVertical: React.FC<IAccountsListVertical> = ({
  linkTo,
  animate = true,
  showDisabled = false,
  updateLocalStorage = true,
}) => {
  const { t } = useTranslation();
  const { enabledAccountsList, allUserAccounts } = useCurrentUser();
  const { updateAccountLocalStorage } = useAccountLocalStorage();

  const accountsList = useMemo(() => {
    return sortBy(showDisabled ? allUserAccounts : enabledAccountsList, "name");
  }, [enabledAccountsList, allUserAccounts, showDisabled]);

  const listItemVariants = {
    hidden: { opacity: 0, y: 50 }, // Inicia desplazado a la derecha
    visible: (index: number) => ({
      opacity: 1,
      y: 0,
      transition: { delay: index * 0.2, duration: 0.3, ease: "easeOut" }, // Delay escalonado
    }),
  };

  if (!accountsList?.length) return null;

  return (
    <motion.ul
      {...(animate
        ? {
            initial: "hidden",
            animate: "visible",
          }
        : {})}
      className={clsx([
        "grid",
        "grid-cols-1",
        "gap-4",
        "md:gap-6",
        "w-full",
        "md:grid-cols-2",
      ])}
    >
      {accountsList?.map?.((account: any, index: number) => {
        return (
          <motion.li
            key={account.account}
            {...(animate
              ? {
                  custom: index,
                  variants: listItemVariants as any,
                  whileHover: { scale: 1.05 },
                  whileTap: { scale: 0.9 },
                  transition: { type: "spring", stiffness: 400, damping: 17 },
                }
              : {})}
          >
            <Link
              to={`/${linkTo}/${account?.account}`}
              onClick={() => {
                if (updateLocalStorage) {
                  updateAccountLocalStorage({ lastAccount: account?.account });
                }
              }}
              replace={linkTo !== "settings/accounts"}
              className={clsx(["md:max-w-[315px]", "md:min-w-[315px]"])}
            >
              <BankCard
                account={account?.account}
                currency={account?.currency}
                bank={account?.bank}
                name={account?.name}
                number={account?.number}
                className={clsx(["w-full"])}
                customStyles={{
                  background: rgbaObjectToString({
                    ...(account?.settings?.bg || { r: 0, g: 0, b: 0 }),
                    a: 1,
                  }),
                  color: rgbaObjectToString({
                    ...(account?.settings?.color || { r: 0, g: 0, b: 0 }),
                    a: 1,
                  }),
                }}
              />
            </Link>
          </motion.li>
        );
      })}
      {!isEven(accountsList?.length) && (
        <motion.li
          className={clsx([
            "hidden",
            "md:flex",
            "w-full",
            "items-center",
            "justify-center",
            // "border",
            "border-solid",
            "rounded-md",
            "text-2xl",
          ])}
          custom={accountsList?.length}
          variants={listItemVariants as any}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.9 }}
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
        >
          <Link
            to="/settings/accounts/new"
            className={clsx([
              "flex",
              "h-full",
              "w-full",
              "items-center",
              "justify-center",
              "flex-row",
              "opacity-40",
            ])}
          >
            <MdAdd size={ICON_SIZES.XL} />
            {t("accounts_page.add_new")}
          </Link>
        </motion.li>
      )}
    </motion.ul>
  );
};

export default memo(AccountsListVertical);
